/**
 * SIM inventory — the ICCID view.
 *
 * The screen the branch was named for, and the first in this product to draw
 * PUSHED readings. That changes what it has to be careful about.
 *
 * ── Every value is drawn with its age, or not drawn ──────────────────────
 *
 * A polled reading is bounded by its own schedule. A pushed one is not: nothing
 * writes a row when a router goes quiet, so the last value simply stays. The
 * predecessor's telemetry table holds rows spanning 717 hours, and a value from
 * thirty days ago renders identically to one from four seconds ago.
 *
 * So a reading past `staleAfterSeconds` is drawn in the stale colour with its
 * age, never as a confident current value. This is the same rule the uptime
 * screen learned the hard way — it drew a green 100.00% for a camera that had
 * been silent for twelve days, and every unit test passed while it did.
 *
 * ── ICCID is not unique, and a collision is shown rather than hidden ─────
 *
 * 🔴 In the recovered fleet data 292 routers reported an ICCID and only 291 were
 * distinct. Two different routers claim the same SIM. Deduplicating would drop a
 * router from an inventory screen, which is the one thing an inventory must
 * never do, so the collision is flagged on both rows instead.
 *
 * ── The modem contradicting itself is shown, not resolved silently ───────
 *
 * Three fleet routers report "No SIM Card" while presenting a valid ICCID, an
 * IMSI, a carrier and an active registration. The ingest believes the identity,
 * which is right — but a modem misreporting its own hardware is a real defect
 * and this is the only screen that can say so.
 *
 * ── Signal and registration are separate columns on purpose ──────────────
 *
 * The strongest signal in the recovered sample belongs to a router the network
 * is REFUSING. Collapsing "how good is the link" and "is it on the network" into
 * one health colour would send someone to check an antenna that is working
 * perfectly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from './api.ts';
import {
  Banner,
  DataTable,
  Empty,
  Loading,
  Pill,
  Section,
  Stat,
  StatRow,
  type ColumnDef,
  type PillState,
} from './components/index.ts';

interface Reading {
  ageMs: number | null;
  staleAfterSeconds: number | null;
}

interface SimRow {
  id: string;
  name: string;
  code: string | null;
  siteCode: string | null;
  siteName: string | null;
  sim: Reading & {
    outcome: string | null;
    reason: string | null;
    iccid: string | null;
    carrier: string | null;
    slot: number | null;
    imsi: string | null;
    statusContradiction: string | null;
  };
  signal: Reading & {
    outcome: string | null;
    reason: string | null;
    rsrpDbm: number | null;
    band: string | null;
    registration: string | null;
    networkType: string | null;
    sinrDb: number | null;
  };
  data: Reading & {
    period: string;
    bytesTotal: number | null;
    rxBytes: number | null;
    txBytes: number | null;
    slots: Array<{ slot: string; rxBytes: number; txBytes: number }> | null;
  };
  duplicateOf: string | null;
}

/** True when a pushed reading is too old to be presented as current. */
export function isStale(reading: Reading): boolean {
  if (reading.ageMs === null || reading.staleAfterSeconds === null) return false;
  return reading.ageMs > reading.staleAfterSeconds * 1000;
}

/** An age an operator can read, from milliseconds. */
export function formatAge(ms: number | null): string {
  if (ms === null) return 'never';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 90) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 90) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * Bytes, in the units a data allowance is sold in.
 *
 * GB and MB — decimal — because that is what appears on a carrier plan, even
 * though the router reports MiB and the stored figure is binary. Labelling a
 * binary number GB would be wrong by 7% at these magnitudes, so the conversion
 * is explicit rather than a relabel.
 */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes === 0) return '0';
  const gb = bytes / 1_000_000_000;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 1 : 2)} GB`;
  const mb = bytes / 1_000_000;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

/**
 * RSRP onto a status band.
 *
 * Thresholds are the conventional LTE ones: better than -80 is strong, -90
 * usable, -100 marginal, below -110 barely a link. Deliberately NOT derived from
 * the check outcome — a registered router with -108 dBm passes its check and is
 * still one storm away from dropping, and that is worth seeing before it does.
 */
export function signalBand(rsrpDbm: number | null): PillState {
  if (rsrpDbm === null) return 'stale';
  if (rsrpDbm >= -90) return 'ok';
  if (rsrpDbm >= -105) return 'warning';
  return 'fault';
}

const REGISTRATION_LABEL: Record<string, string> = {
  registered: 'Registered',
  denied: 'Refused',
  searching: 'Searching',
  unknown: 'Unknown',
};

const REGISTRATION_BAND: Record<string, PillState> = {
  registered: 'ok',
  denied: 'fault',
  searching: 'fault',
  unknown: 'stale',
};

const STALE_REASON =
  'Pushed readings have no schedule — nothing writes a row when a router stops reporting.';

/**
 * The value cell for a reading, in one place.
 *
 * Every pushed value on this screen goes through here, so none of them can be
 * rendered without its age. Passing the reading rather than the formatted
 * string is what makes that structural instead of a habit. With a `band` the
 * value is a status and draws as a pill; without one it is a plain figure.
 */
function Value({
  reading,
  children,
  band,
}: {
  reading: Reading;
  children: React.ReactNode;
  band?: PillState;
}): React.JSX.Element {
  const stale = isStale(reading);
  const age = stale ? `${formatAge(reading.ageMs)} · stale` : null;
  return (
    <>
      {band !== undefined ? (
        <Pill state={stale ? 'stale' : band} reason={stale ? `${age}. ${STALE_REASON}` : null}>
          {children}
        </Pill>
      ) : (
        <span className={stale ? 'muted' : undefined}>{children}</span>
      )}
      {age !== null && <div className="small caveat">{age}</div>}
    </>
  );
}

export function SimView(): React.JSX.Element {
  const [rows, setRows] = useState<SimRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ assets: SimRow[] }>('/api/sim');
      setRows(result.assets);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not load SIM inventory');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<SimRow, unknown>[]>(
    () => [
      {
        id: 'gateway',
        header: 'Gateway',
        accessorFn: (r) => r.code ?? r.name,
        cell: ({ row }) => (
          <>
            <div>{row.original.name}</div>
            <div className="cell-sub">{row.original.code ?? '—'}</div>
          </>
        ),
      },
      {
        id: 'site',
        header: 'Site',
        accessorFn: (r) => r.siteCode ?? '',
        cell: ({ getValue }) => <span className="small muted">{(getValue() as string) || '—'}</span>,
      },
      {
        id: 'iccid',
        header: 'ICCID',
        accessorFn: (r) => r.sim.iccid ?? '',
        cell: ({ row }) => {
          const { sim } = row.original;
          return (
            <div className="mono">
              {sim.iccid === null ? (
                <Pill state="stale" reason={sim.reason}>
                  {sim.outcome === null ? 'never reported' : 'no ICCID'}
                </Pill>
              ) : (
                <Value reading={sim}>{sim.iccid}</Value>
              )}
              {row.original.duplicateOf !== null && (
                <div>
                  <Pill
                    state="fault"
                    reason="Two assets report the same ICCID. One of them is wrong, or a SIM was moved and one router is reporting stale."
                  >
                    also on another gateway
                  </Pill>
                </div>
              )}
              {sim.iccid !== null && sim.reason !== null && sim.outcome !== 'pass' && (
                <div className="small muted">{sim.reason}</div>
              )}
            </div>
          );
        },
      },
      {
        id: 'carrier',
        header: 'Carrier',
        accessorFn: (r) => r.sim.carrier ?? '',
        cell: ({ getValue }) => <span className="small">{(getValue() as string) || '—'}</span>,
      },
      {
        id: 'slot',
        header: 'SIM',
        accessorFn: (r) => r.sim.slot ?? -1,
        meta: { align: 'right' },
        cell: ({ row }) => <span className="mono">{row.original.sim.slot ?? '—'}</span>,
      },
      {
        id: 'network',
        header: 'Network',
        accessorFn: (r) => r.signal.registration ?? 'unknown',
        cell: ({ row }) => {
          const registration = row.original.signal.registration ?? 'unknown';
          return (
            <>
              <Pill state={REGISTRATION_BAND[registration] ?? 'stale'} reason={row.original.signal.reason}>
                {REGISTRATION_LABEL[registration] ?? registration}
              </Pill>
              {row.original.signal.networkType !== null && (
                <div className="small muted">{row.original.signal.networkType}</div>
              )}
            </>
          );
        },
      },
      {
        id: 'rsrp',
        header: 'RSRP',
        accessorFn: (r) => r.signal.rsrpDbm ?? -999,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const { signal } = row.original;
          return (
            <div className="mono">
              {signal.rsrpDbm === null ? (
                <span className="muted">—</span>
              ) : (
                <Value reading={signal} band={signalBand(signal.rsrpDbm)}>
                  {signal.rsrpDbm} dBm
                </Value>
              )}
              {signal.sinrDb !== null && <div className="small muted">SINR {String(signal.sinrDb)} dB</div>}
            </div>
          );
        },
      },
      {
        id: 'band',
        header: 'Band',
        accessorFn: (r) => r.signal.band ?? '',
        cell: ({ getValue }) => (
          <span className="small mono">{(getValue() as string) || <span className="muted">not reported</span>}</span>
        ),
      },
      {
        id: 'data',
        header: 'Data (month)',
        accessorFn: (r) => r.data.bytesTotal ?? -1,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const { data } = row.original;
          return (
            <div className="mono">
              <Value reading={data}>{formatBytes(data.bytesTotal)}</Value>
              {data.slots !== null && data.slots.length > 1 && (
                <div className="small muted">
                  {data.slots
                    .filter((s) => s.rxBytes + s.txBytes > 0)
                    .map((s) => `${s.slot} ${formatBytes(s.rxBytes + s.txBytes)}`)
                    .join(' · ') || 'no traffic on either slot'}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: 'heard',
        header: 'Last heard',
        accessorFn: (r) => r.sim.ageMs ?? Number.MAX_SAFE_INTEGER,
        meta: { align: 'right' },
        cell: ({ row }) => <span className="small muted">{formatAge(row.original.sim.ageMs)}</span>,
      },
    ],
    [],
  );

  const contradictionColumns = useMemo<ColumnDef<SimRow, unknown>[]>(
    () => [
      {
        id: 'gateway',
        header: 'Gateway',
        accessorFn: (r) => r.code ?? r.name,
        cell: ({ getValue }) => <span className="mono">{getValue() as string}</span>,
      },
      {
        id: 'reported',
        header: 'Reported',
        accessorFn: (r) => r.sim.statusContradiction ?? '',
        enableSorting: false,
      },
    ],
    [],
  );

  if (error !== null) return <Banner tone="fault">{error}</Banner>;
  if (rows === null) return <Loading rows={8} cols={10} label="Loading SIM inventory" />;

  const reporting = rows.filter((r) => r.sim.outcome !== null);
  const silent = rows.filter((r) => r.sim.outcome === null);
  const stale = reporting.filter((r) => isStale(r.sim));
  const contradictions = rows.filter((r) => r.sim.statusContradiction !== null);
  const duplicates = rows.filter((r) => r.duplicateOf !== null);
  const totalBytes = rows.reduce(
    (sum, r) => sum + (isStale(r.data) ? 0 : (r.data.bytesTotal ?? 0)),
    0,
  );

  const summary =
    'Pushed by the routers, not polled — a value past its freshness window is shown stale with its age. ' +
    'Data is month-to-date; RSRP rather than signal bars, since RSRP is the one that predicts a drop.' +
    (totalBytes > 0
      ? ` Fresh readings: ${formatBytes(totalBytes)} across ${rows.length} gateway${rows.length === 1 ? '' : 's'}.`
      : '');

  return (
    <div className="stack">
      <StatRow>
        <Stat n={reporting.length} label="Reporting" state={reporting.length === 0 ? 'stale' : 'ok'} />
        <Stat n={silent.length} label="Never reported" state="warning" />
        <Stat n={stale.length} label="Stale" state="warning" />
        <Stat n={duplicates.length} label="Duplicate ICCID" state="fault" />
      </StatRow>

      {contradictions.length > 0 && (
        <Section
          flush
          title="Modems contradicting themselves"
          summary="Report no SIM card while presenting one. The identity is believed; the misreport is a firmware defect worth chasing."
        >
          <DataTable
            data={contradictions}
            columns={contradictionColumns}
            getRowId={(r) => r.id}
            caption="Modems contradicting themselves"
          />
        </Section>
      )}

      <Section flush title="Gateways" summary={summary}>
        <DataTable
          data={rows}
          columns={columns}
          getRowId={(r) => r.id}
          initialSort={[{ id: 'gateway', desc: false }]}
          rowClass={(r) => (r.duplicateOf !== null ? 'row-fault' : undefined)}
          minWidth={960}
          caption="SIM inventory"
          empty={<Empty title="No asset in this tenant has a driver that reports SIM identity" />}
        />
      </Section>
    </div>
  );
}
